import { useTranslation } from 'react-i18next';
import PublicContentPage, { PublicCtaLink } from '../components/layout/PublicContentPage';
import { buildArticleSchema } from '../lib/seo';

const GUIDE_PATH = '/accessibility-compliance-report-for-procurement';
const GUIDE_DATE = '2026-05-21';

interface GuideSection {
  title: string;
  body: string;
  points?: string[];
}

interface GuideStep {
  title: string;
  body: string;
}

interface GuideResource {
  href: string;
  label: string;
  description: string;
}

interface GuideAction {
  href: string;
  label: string;
  variant: 'primary' | 'secondary' | 'ghost';
}

export default function ProcurementReportGuidePage() {
  const { t } = useTranslation('marketing');
  const evidencePoints = t('pages.procurementReportGuide.why.points', { returnObjects: true }) as string[];
  const reportChecklist = t('pages.procurementReportGuide.reportChecklist.items', { returnObjects: true }) as string[];
  const workflowSteps = t('pages.procurementReportGuide.workflow.steps', { returnObjects: true }) as GuideStep[];
  const sections = t('pages.procurementReportGuide.sections', { returnObjects: true }) as GuideSection[];
  const relatedResources = t('pages.procurementReportGuide.related.resources', { returnObjects: true }) as GuideResource[];
  const sidebarPoints = t('pages.procurementReportGuide.sidebar.points', { returnObjects: true }) as string[];
  const ctaActions = t('pages.procurementReportGuide.cta.actions', { returnObjects: true }) as GuideAction[];

  return (
    <PublicContentPage
      metaTitle={t('pages.procurementReportGuide.meta.title')}
      metaDescription={t('pages.procurementReportGuide.meta.description')}
      canonicalPath={GUIDE_PATH}
      eyebrow={t('pages.procurementReportGuide.hero.eyebrow')}
      title={t('pages.procurementReportGuide.hero.title')}
      intro={t('pages.procurementReportGuide.hero.intro')}
      metaType="article"
      structuredData={[
        buildArticleSchema({
          headline: t('pages.procurementReportGuide.hero.title'),
          description: t('pages.procurementReportGuide.meta.description'),
          path: GUIDE_PATH,
          datePublished: GUIDE_DATE,
          dateModified: GUIDE_DATE,
        }),
      ]}
      aside={(
        <>
          <section className="acc-public-page-card acc-public-page-card--sticky">
            <h2>{t('pages.procurementReportGuide.sidebar.title')}</h2>
            <p>{t('pages.procurementReportGuide.sidebar.body')}</p>
            <ul className="acc-public-page-list">
              {sidebarPoints.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          </section>

          <section className="acc-public-page-card">
            <h2>{t('pages.procurementReportGuide.sidebar.noteTitle')}</h2>
            <p>{t('pages.procurementReportGuide.sidebar.noteBody')}</p>
          </section>
        </>
      )}
    >
      <section className="acc-public-page-card">
        <h2>{t('pages.procurementReportGuide.intro.title')}</h2>
        <p>{t('pages.procurementReportGuide.intro.body')}</p>
        <p>{t('pages.procurementReportGuide.intro.note')}</p>
      </section>

      <section className="acc-public-page-card">
        <h2>{t('pages.procurementReportGuide.why.title')}</h2>
        <p>{t('pages.procurementReportGuide.why.body')}</p>
        <ul className="acc-public-page-list">
          {evidencePoints.map((point) => (
            <li key={point}>{point}</li>
          ))}
        </ul>
      </section>

      <section className="acc-public-page-card">
        <h2>{t('pages.procurementReportGuide.reportChecklist.title')}</h2>
        <ul className="acc-public-page-list" aria-label={t('pages.procurementReportGuide.reportChecklist.ariaLabel')}>
          {reportChecklist.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>

      <section className="acc-public-page-section" aria-labelledby="procurement-workflow-title">
        <div>
          <h2 id="procurement-workflow-title">{t('pages.procurementReportGuide.workflow.title')}</h2>
          <p>{t('pages.procurementReportGuide.workflow.body')}</p>
        </div>

        <div className="acc-public-page-grid">
          {workflowSteps.map((step, index) => (
            <article key={step.title} className="acc-public-page-subcard">
              <h3>
                {t('pages.procurementReportGuide.workflow.stepLabel', { number: index + 1 })}
                {' '}
                {step.title}
              </h3>
              <p>{step.body}</p>
            </article>
          ))}
        </div>
      </section>

      {sections.map((section) => (
        <section key={section.title} className="acc-public-page-card">
          <h2>{section.title}</h2>
          <p>{section.body}</p>
          {section.points && section.points.length > 0 && (
            <ul className="acc-public-page-list">
              {section.points.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          )}
        </section>
      ))}

      <section className="acc-public-page-section" aria-labelledby="procurement-related-title">
        <div>
          <h2 id="procurement-related-title">{t('pages.procurementReportGuide.related.title')}</h2>
          <p>{t('pages.procurementReportGuide.related.body')}</p>
        </div>
        <div className="acc-public-page-grid">
          {relatedResources.map((resource) => (
            <article key={resource.href} className="acc-public-page-subcard">
              <h3>{resource.label}</h3>
              <p>{resource.description}</p>
              <div className="acc-public-page-actions">
                <PublicCtaLink href={resource.href} variant="ghost">
                  {resource.label}
                </PublicCtaLink>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="acc-public-page-card">
        <h2>{t('pages.procurementReportGuide.disclaimer.title')}</h2>
        <p>{t('pages.procurementReportGuide.disclaimer.body')}</p>
      </section>

      <section className="acc-public-cta-band" aria-labelledby="procurement-guide-cta-title">
        <div>
          <h2 id="procurement-guide-cta-title">{t('pages.procurementReportGuide.cta.title')}</h2>
          <p>{t('pages.procurementReportGuide.cta.body')}</p>
        </div>
        <div className="acc-public-cta-actions">
          {ctaActions.map((action) => (
            <PublicCtaLink key={action.href} href={action.href} variant={action.variant}>
              {action.label}
            </PublicCtaLink>
          ))}
        </div>
      </section>
    </PublicContentPage>
  );
}
